import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Filter, Loader2, Play, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn, formatAmount } from '@/lib/utils';
import { useTenantId } from '@/lib/auth/hooks';
import { CubixApiError } from '@/lib/api/types';

import { AnalysisSkeleton } from '@/components/shared/AnalysisSkeleton';

import { useLatestGap, useSlicedGap, useTriggerCalculation } from './api';
import { GapBucketsChart } from './GapBucketsChart';
import type {
  BucketView,
  CashflowFilters,
  GapAnalysisDetail,
  GapBucket,
  PresentationMode,
} from './types';
import { isFilterActive } from './types';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function GapAnalysisPage() {
  const tenantId = useTenantId();
  const [date, setDate] = useState<string>(todayISO());

  // Phase 2.1.1 / 2.2 — both toggles live in URL search params for
  // linkable / shareable views.
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: PresentationMode =
    (searchParams.get('mode')?.toUpperCase() === 'PRINCIPAL') ? 'PRINCIPAL' : 'FLOW';
  const view: BucketView =
    (searchParams.get('view')?.toUpperCase() === 'SHORT_MEDIUM_LONG')
      ? 'SHORT_MEDIUM_LONG'
      : 'STANDARD';
  function setMode(next: PresentationMode) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'FLOW') sp.delete('mode'); else sp.set('mode', next);
    setSearchParams(sp, { replace: true });
  }
  function setView(next: BucketView) {
    const sp = new URLSearchParams(searchParams);
    if (next === 'STANDARD') sp.delete('view'); else sp.set('view', next);
    setSearchParams(sp, { replace: true });
  }

  // Phase 2.2.B — cashflow-dimension filters mirrored into URL params.
  const filters: CashflowFilters = useMemo(() => ({
    currency:       searchParams.get('ccy')        ?? undefined,
    instrumentType: searchParams.get('instType')   ?? undefined,
    cfType:         (searchParams.get('cfType')?.toUpperCase() === 'INFLOW'
                      ? 'INFLOW'
                      : searchParams.get('cfType')?.toUpperCase() === 'OUTFLOW'
                        ? 'OUTFLOW'
                        : undefined) as CashflowFilters['cfType'],
    instrument:     searchParams.get('instrument') ?? undefined,
  }), [searchParams]);
  function patchFilters(patch: Partial<CashflowFilters>) {
    const sp = new URLSearchParams(searchParams);
    const set = (k: string, v: string | undefined) => {
      if (v && v.trim() !== '') sp.set(k, v.trim()); else sp.delete(k);
    };
    if ('currency'       in patch) set('ccy',        patch.currency);
    if ('instrumentType' in patch) set('instType',   patch.instrumentType);
    if ('cfType'         in patch) set('cfType',     patch.cfType);
    if ('instrument'     in patch) set('instrument', patch.instrument);
    setSearchParams(sp, { replace: true });
  }
  const filterActive = isFilterActive(filters);

  // Latest persisted result OR sliced/ephemeral, depending on filter state.
  // We keep both hooks mounted (TanStack Query handles enabled/disabled);
  // only one of the two is actually fetching at any given moment.
  const latest = useLatestGap(date, view, { enabled: !filterActive });
  const sliced = useSlicedGap(date, filters, view, { enabled: filterActive });
  const trigger = useTriggerCalculation();

  const active  = filterActive ? sliced : latest;
  const detail: GapAnalysisDetail | null = active.data ?? null;

  const visibleBuckets: GapBucket[] =
    mode === 'PRINCIPAL'
      ? (detail?.principalBuckets ?? [])
      : (detail?.buckets ?? []);
  const principalAvailable = (detail?.principalBuckets?.length ?? 0) > 0;

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Gap Analysis</h1>
          <p className="text-sm text-muted-foreground">
            Interest-rate and liquidity gap by maturity bucket — tenant{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{tenantId ?? '—'}</code>.
          </p>
        </div>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Run a calculation</CardTitle>
          <CardDescription>
            Reads the latest result for the chosen date, or triggers a fresh on-demand calculation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              trigger.mutate({ calculationDate: date, force: true });
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="calc-date">Calculation date</Label>
              <Input
                id="calc-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-48"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => active.refetch()}
              disabled={active.isFetching}
            >
              {active.isFetching ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Reload
            </Button>
            <Button type="submit" disabled={trigger.isPending}>
              {trigger.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              Run on-demand
            </Button>
          </form>

          {trigger.isSuccess ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Calculation queued · result_id{' '}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">
                {trigger.data.resultId}
              </code>
              {' · '}engine {trigger.data.engineVersion}
            </p>
          ) : null}
          {trigger.isError ? <ApiErrorBanner error={trigger.error} /> : null}
        </CardContent>
      </Card>

      {active.isError ? <ApiErrorBanner error={active.error} /> : null}

      <FilterBar
        filters={filters}
        onChange={patchFilters}
        active={filterActive}
      />

      <div className="flex flex-wrap items-center gap-3">
        <ModeBar
          mode={mode}
          onChange={setMode}
          principalAvailable={principalAvailable}
        />
        <ViewSelector value={view} onChange={setView} />
      </div>
      {active.isLoading && !detail ? (
        <AnalysisSkeleton />
      ) : (
        <>
          <SummaryTiles detail={detail} loading={active.isLoading} sliced={filterActive} />
          <GapBucketsChart buckets={visibleBuckets} />
          <BucketsTable
            buckets={visibleBuckets}
            loading={active.isLoading}
            mode={mode}
          />
        </>
      )}
    </div>
  );
}

/**
 * Phase 2.2.B — cashflow-dimension filters. Each control writes through
 * onChange; parent component persists the values into URL search params.
 * "Active" highlight surfaces when at least one dimension is set so the
 * analyst remembers they're looking at a slice, not the canonical result.
 */
function FilterBar({
  filters,
  onChange,
  active,
}: {
  filters: CashflowFilters;
  onChange: (patch: Partial<CashflowFilters>) => void;
  active: boolean;
}) {
  return (
    <Card className={cn(active ? 'border-amber-500/40' : '')}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Filter className="h-4 w-4" />
          Cashflow filters
          {active ? (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
              Slice
            </span>
          ) : null}
        </CardTitle>
        <CardDescription>
          Narrow the gap to a slice of the cashflow stream. Slices are
          ephemeral — they don't replace the saved calculation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="f-ccy" className="text-xs">Currency</Label>
            <Input
              id="f-ccy"
              placeholder="MAD · EUR · USD"
              maxLength={3}
              value={filters.currency ?? ''}
              onChange={(e) => onChange({ currency: e.target.value.toUpperCase() })}
              className="h-8"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-itype" className="text-xs">Instrument type</Label>
            <Input
              id="f-itype"
              placeholder="Bond · Loan · Deposit"
              value={filters.instrumentType ?? ''}
              onChange={(e) => onChange({ instrumentType: e.target.value })}
              className="h-8"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-cftype" className="text-xs">Direction</Label>
            <select
              id="f-cftype"
              value={filters.cfType ?? ''}
              onChange={(e) =>
                onChange({
                  cfType: e.target.value === ''
                    ? undefined
                    : (e.target.value as CashflowFilters['cfType']),
                })
              }
              className={cn(
                'flex h-8 w-full rounded-md border bg-background px-2 text-xs',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              <option value="">— any —</option>
              <option value="INFLOW">INFLOW (assets)</option>
              <option value="OUTFLOW">OUTFLOW (liabilities)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-instrument" className="text-xs">
              Instrument id contains
            </Label>
            <Input
              id="f-instrument"
              placeholder="BO123, LN-, etc."
              value={filters.instrument ?? ''}
              onChange={(e) => onChange({ instrument: e.target.value })}
              className="h-8"
            />
          </div>
        </div>
        {active ? (
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                onChange({
                  currency: undefined,
                  instrumentType: undefined,
                  cfType: undefined,
                  instrument: undefined,
                })
              }
            >
              <X className="mr-1 h-3.5 w-3.5" />
              Clear filters
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Phase 2.2 — bucket view selector. STANDARD = atomic 7 buckets;
 * SHORT_MEDIUM_LONG = 3 coarse bands suitable for execs.
 */
function ViewSelector({
  value,
  onChange,
}: {
  value: BucketView;
  onChange: (next: BucketView) => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor="bucket-view" className="text-xs text-muted-foreground">
        View
      </Label>
      <select
        id="bucket-view"
        value={value}
        onChange={(e) => onChange(e.target.value as BucketView)}
        className={cn(
          'h-8 rounded-md border bg-background px-2 text-xs font-medium',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        )}
      >
        <option value="STANDARD">Standard (7 buckets)</option>
        <option value="SHORT_MEDIUM_LONG">Short / Medium / Long (3 bands)</option>
      </select>
    </div>
  );
}

/**
 * Two-button segmented control + a hint when the loaded result has no
 * principal data (e.g. older calculation that pre-dated V110).
 */
function ModeBar({
  mode,
  onChange,
  principalAvailable,
}: {
  mode: PresentationMode;
  onChange: (next: PresentationMode) => void;
  principalAvailable: boolean;
}) {
  const opts: Array<{ id: PresentationMode; label: string; sub: string }> = [
    { id: 'FLOW',      label: 'Flow',      sub: 'tombée' },
    { id: 'PRINCIPAL', label: 'Principal', sub: 'encours' },
  ];
  return (
    <div className="flex items-center gap-3">
      <div
        className="inline-flex items-center gap-1 rounded-md border bg-muted/40 p-0.5"
        role="tablist"
        aria-label="Presentation mode"
      >
        {opts.map((o) => {
          const active = mode === o.id;
          const disabled = o.id === 'PRINCIPAL' && !principalAvailable;
          return (
            <button
              key={o.id}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={disabled}
              onClick={() => onChange(o.id)}
              className={cn(
                'flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium',
                'transition-colors',
                active
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
                disabled && 'opacity-50 hover:text-muted-foreground cursor-not-allowed',
              )}
              title={
                disabled
                  ? 'Principal view is not available for this calculation (recalculate to populate it).'
                  : undefined
              }
            >
              <span>{o.label}</span>
              <span className="text-[10px] italic text-muted-foreground/80">({o.sub})</span>
            </button>
          );
        })}
      </div>
      {!principalAvailable && mode === 'FLOW' ? (
        <p className="text-xs text-muted-foreground">
          Principal (encours) view requires a calculation produced after V110 — re-run on-demand to populate it.
        </p>
      ) : null}
    </div>
  );
}

function SummaryTiles({
  detail,
  loading,
  sliced,
}: {
  detail: GapAnalysisDetail | null;
  loading: boolean;
  sliced?: boolean;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Tile
        title={sliced ? 'Total Net Gap (filtered)' : 'Total Net Gap'}
        value={detail ? formatAmount(detail.totalNetGap) : '—'}
        hint={
          loading
            ? 'Loading…'
            : detail
              ? sliced
                ? 'Sum across the filtered cashflow slice (ephemeral, not saved)'
                : 'Sum of bucket net gaps'
              : 'No result yet'
        }
      />
      <Tile
        title="Liquidity Shortfall"
        value={detail ? (detail.hasLiquidityShortfall ? 'Yes' : 'No') : '—'}
        hint={
          detail?.hasLiquidityShortfall
            ? 'At least one bucket is negative'
            : detail
              ? 'All buckets non-negative'
              : '—'
        }
        tone={detail?.hasLiquidityShortfall ? 'warn' : 'ok'}
      />
      <Tile
        title="LCR"
        value={detail?.liquidityRatios?.lcrRatio ?? '—'}
        hint={
          detail?.liquidityRatios
            ? Number(detail.liquidityRatios.lcrRatio) >= 1
              ? 'Compliant (≥ 100%)'
              : 'Below 100%'
            : 'Not yet returned by cx-alm'
        }
      />
      <Tile
        title="NSFR"
        value={detail?.liquidityRatios?.nsfrRatio ?? '—'}
        hint={
          detail?.liquidityRatios
            ? Number(detail.liquidityRatios.nsfrRatio) >= 1
              ? 'Compliant (≥ 100%)'
              : 'Below 100%'
            : 'Not yet returned by cx-alm'
        }
      />
    </div>
  );
}

function Tile({
  title,
  value,
  hint,
  tone,
}: {
  title: string;
  value: string;
  hint?: string;
  tone?: 'ok' | 'warn';
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle
          className={
            tone === 'warn' ? 'text-2xl text-destructive' : 'text-2xl'
          }
        >
          {value}
        </CardTitle>
      </CardHeader>
      {hint ? (
        <CardContent className="pt-0 text-xs text-muted-foreground">{hint}</CardContent>
      ) : null}
    </Card>
  );
}

function BucketsTable({
  buckets,
  loading,
  mode,
}: {
  buckets: GapBucket[];
  loading: boolean;
  mode: PresentationMode;
}) {
  const isPrincipal = mode === 'PRINCIPAL';
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Maturity buckets &middot;{' '}
          <span className="text-muted-foreground font-normal">
            {isPrincipal ? 'Principal (encours)' : 'Flow (tombée)'}
          </span>
        </CardTitle>
        <CardDescription>
          {isPrincipal
            ? 'Outstanding balance at the end of each bucket. Assets / liabilities columns hold the residual balance per side.'
            : 'Cashflows occurring within each bucket. Assets inflow / liabilities outflow are the cash movements during the period.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">#</th>
                <th className="px-4 py-2 text-left font-medium">Bucket</th>
                <th className="px-4 py-2 text-right font-medium">
                  {isPrincipal ? 'Assets outstanding' : 'Assets inflow'}
                </th>
                <th className="px-4 py-2 text-right font-medium">
                  {isPrincipal ? 'Liabilities outstanding' : 'Liabilities outflow'}
                </th>
                <th className="px-4 py-2 text-right font-medium">Net</th>
                <th className="px-4 py-2 text-right font-medium">
                  {isPrincipal ? 'Net (running)' : 'Cumulative gap'}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-center text-muted-foreground" colSpan={6}>
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
                  </td>
                </tr>
              ) : buckets.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-muted-foreground" colSpan={6}>
                    No buckets yet — run an on-demand calculation above.
                  </td>
                </tr>
              ) : (
                buckets.map((b) => {
                  const negative = Number(b.netGap) < 0;
                  return (
                    <tr key={b.bucketIndex} className="border-b last:border-0">
                      <td className="px-4 py-2 text-muted-foreground">{b.bucketIndex}</td>
                      <td className="px-4 py-2 font-medium">{b.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatAmount(b.assetsInflow)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatAmount(b.liabilitiesOutflow)}
                      </td>
                      <td
                        className={
                          'px-4 py-2 text-right tabular-nums ' +
                          (negative ? 'text-destructive' : '')
                        }
                      >
                        {formatAmount(b.netGap)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {formatAmount(b.cumulativeGap)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ApiErrorBanner({ error }: { error: CubixApiError | Error }) {
  const isCubix = error instanceof CubixApiError;
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{error.message}</p>
        {isCubix ? (
          <p className="mt-0.5 text-xs opacity-80">
            code <code className="rounded bg-background/60 px-1 py-0.5">{error.code}</code>
            {error.field ? <> · field {error.field}</> : null}
            {error.traceId ? <> · trace {error.traceId}</> : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
